// src/components/routing/TeamManagementComponent.js
// FlowSense AI — Enhanced Team Management v2.0
// Glass Morphism, Framer Motion Animations, Performance Optimized
// --- UPDATE (FlowSense v3 · AI Explainer)
//   • Real team data: member count per team (teamService.getUsersInTeam) and how many enabled
//     assignment rules route work to it (assignmentRuleService), loaded after the list, 4 at a time.
//   • New "Routing" column and "Without members" stat.
//   • FlowSense AI explainer: flags teams that receive rule-based work but have no members
//     (jobs routed to nobody), teams without a lead, empty teams and teams no rule uses.
//   • Sort / filter by "needs attention"; CSV export.
// --- END UPDATE

import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
    Box, Button, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Spinner, Alert,
    AlertIcon, HStack, useToast, Heading, Flex, IconButton, useDisclosure,
    AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent,
    AlertDialogOverlay, Text, Badge, Tooltip, InputGroup, InputLeftElement, Input,
    InputRightElement, Select, Menu, MenuButton, MenuList, MenuItem, Avatar, VStack,
    Card, CardBody, Stat, StatLabel, StatNumber, StatHelpText, SimpleGrid,
    useColorModeValue, Icon, Skeleton, SkeletonCircle, SkeletonText, Progress,
    Tag, TagLabel, TagLeftIcon,
} from '@chakra-ui/react';
import {
    AddIcon, EditIcon, DeleteIcon, SearchIcon, ChevronDownIcon, ViewIcon, CloseIcon,
} from '@chakra-ui/icons';
import {
    MdGroups, MdPersonAdd, MdLeaderboard, MdWarning, MdRefresh,
    MdStar, MdShield, MdTrendingUp, MdFilterList,
} from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import * as teamService from '../../services/teamService';
import TeamFormModal from './TeamFormModal';
import TeamMembersModal from './TeamMembersModal';
// --- UPDATE (AI Explainer)
import * as assignmentRuleService from '../../services/assignmentRuleService';
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer';
/** Run async fn over items, `limit` at a time. */
const mapLimit = async (items, limit, fn) => {
    const out = new Array(items.length);
    let i = 0;
    const worker = async () => { while (i < items.length) { const k = i++; try { out[k] = await fn(items[k]); } catch (_) { out[k] = null; } } };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
    return out;
};
// --- END UPDATE

// ─────────────────────────────────────────────────────────────────────────────
// MOTION WRAPPERS & ANIMATION PRESETS
// ─────────────────────────────────────────────────────────────────────────────
const MotionBox  = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr   = motion(Tr);

const ANIM = {
    fadeInUp:   { initial:{ opacity:0, y:16 },  animate:{ opacity:1, y:0 } },
    fadeInDown: { initial:{ opacity:0, y:-16 }, animate:{ opacity:1, y:0 } },
    fadeInLeft: { initial:{ opacity:0, x:-16 }, animate:{ opacity:1, x:0 } },
    scaleIn:    { initial:{ opacity:0, scale:0.92 }, animate:{ opacity:1, scale:1 } },
    stagger:    (i, base=0.05) => ({ delay: i * base }),
    spring:     { type:'spring', stiffness:300, damping:24 },
    smooth:     { duration:0.3, ease:[0.25, 0.46, 0.45, 0.94] },
};

// ─────────────────────────────────────────────────────────────────────────────
// SHARED SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

/** Live pulse dot */
const PulseIndicator = ({ color='green', size=2 }) => (
    <Box position="relative" display="inline-flex" w={`${size*4}px`} h={`${size*4}px`} flexShrink={0}>
        <MotionBox position="absolute" inset={0} borderRadius="full" bg={`${color}.400`}
            animate={{ scale:[1,1.8,1], opacity:[0.7,0,0.7] }}
            transition={{ duration:2, repeat:Infinity, ease:'easeInOut' }} />
        <Box position="absolute" inset={0} borderRadius="full" bg={`${color}.500`} />
    </Box>
);

/** Enhanced stat card with gradient accent */
const EnhancedStatCard = memo(({ label, value, helpText, icon, color='blue', gradient, isLoading: loading }) => {
    const cardBg      = useColorModeValue('white','gray.800');
    const borderColor = useColorModeValue('gray.200','gray.600');
    const mutedColor  = useColorModeValue('gray.500','gray.400');

    return (
        <MotionBox whileHover={{ y:-3, boxShadow:'0 8px 25px rgba(0,0,0,0.1)' }} transition={{ duration:0.2 }}>
            <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl"
                overflow="hidden" position="relative">
                <Box position="absolute" top={0} left={0} right={0} h="3px"
                    bgGradient={gradient || `linear(to-r, ${color}.400, ${color}.600)`} />
                <CardBody py={4} px={5}>
                    {loading ? (
                        <VStack align="start" spacing={2}>
                            <Skeleton h="12px" w="80px" borderRadius="md" />
                            <Skeleton h="28px" w="50px" borderRadius="md" />
                            <Skeleton h="10px" w="100px" borderRadius="md" />
                        </VStack>
                    ) : (
                        <HStack justify="space-between" align="start">
                            <Stat size="sm">
                                <StatLabel fontSize="xs" fontWeight="600" color={mutedColor}
                                    textTransform="uppercase" letterSpacing="wider">
                                    {label}
                                </StatLabel>
                                <StatNumber fontSize="2xl" fontWeight="800" color={`${color}.500`}>
                                    {value}
                                </StatNumber>
                                <StatHelpText fontSize="xs" mb={0}>{helpText}</StatHelpText>
                            </Stat>
                            {icon && (
                                <Box p={2} borderRadius="xl" bg={`${color}.50`}>
                                    <Icon as={icon} boxSize={5} color={`${color}.500`} />
                                </Box>
                            )}
                        </HStack>
                    )}
                </CardBody>
            </Card>
        </MotionBox>
    );
});

/** Animated empty state */
const EmptyState = memo(({ icon:IconComp, title, subtitle }) => (
    <Flex direction="column" align="center" justify="center" py={10} gap={3}>
        <MotionBox animate={{ y:[0,-6,0] }} transition={{ duration:3, repeat:Infinity, ease:'easeInOut' }}>
            <Icon as={IconComp || MdGroups} boxSize={14} color="gray.300" />
        </MotionBox>
        <Text fontWeight="700" color="gray.400" fontSize="md">{title}</Text>
        {subtitle && <Text fontSize="sm" color="gray.400" textAlign="center" maxW="300px">{subtitle}</Text>}
    </Flex>
));

/** Table skeleton rows */
const TableSkeleton = memo(({ rows=5, cols=5 }) => {
    const skBg = useColorModeValue('gray.100','gray.700');
    return Array.from({ length:rows }).map((_, i) => (
        <Tr key={i}>
            {Array.from({ length:cols }).map((_, j) => (
                <Td key={j}><Skeleton h="16px" w={j === 0 ? '140px' : '100px'} borderRadius="md" startColor={skBg} /></Td>
            ))}
        </Tr>
    ));
});

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const TeamManagementComponent = () => {
    const [teams, setTeams] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [teamToDelete, setTeamToDelete] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterLeadStatus, setFilterLeadStatus] = useState('all');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');
    // --- UPDATE
    const [memberCounts, setMemberCounts] = useState({}); // teamId → number | null (unknown)
    const [rulesByTeam, setRulesByTeam] = useState({});   // teamId → enabled rule count
    const [rulesLoaded, setRulesLoaded] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    // --- END UPDATE

    const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const { isOpen: isMembersOpen, onOpen: onMembersOpen, onClose: onMembersClose } = useDisclosure();
    const cancelRef = React.useRef();
    const toast = useToast();

    // Theme tokens — all unconditional at top
    const bgColor       = useColorModeValue('white', 'gray.800');
    const borderColor   = useColorModeValue('gray.200', 'gray.600');
    const trHoverBg     = useColorModeValue('gray.50', 'gray.700');
    const mutedColor    = useColorModeValue('gray.500', 'gray.400');
    const searchBg      = useColorModeValue('white', 'gray.700');
    const headerGradient= useColorModeValue(
        'linear(to-r, blue.500, purple.500)',
        'linear(to-r, blue.400, purple.400)'
    );

    // ── Data Fetching (original logic preserved) ─────────────────────────────
    const fetchTeamsData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await teamService.fetchTeams();
            setTeams(Array.isArray(data) ? data : []);
        } catch (err) {
            const errorMessage = err?.response?.data?.message || err?.message || 'Failed to fetch teams data.';
            setError(errorMessage);
            toast({ title:'Error fetching teams', description:errorMessage, status:'error', duration:5000, isClosable:true });
        } finally {
            setIsLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        fetchTeamsData();
    }, [fetchTeamsData]);

    // --- UPDATE: members + routing per team (after the list is on screen)
    useEffect(() => {
        if (!teams.length) return undefined;
        let cancelled = false;
        (async () => {
            setDetailsLoading(true);
            try {
                const [rules, counts] = await Promise.all([
                    assignmentRuleService.fetchAssignmentRules().catch(() => null),
                    mapLimit(teams, 4, async (t) => {
                        if (Number.isFinite(Number(t.member_count))) return Number(t.member_count);
                        const m = await teamService.getUsersInTeam(t.id);
                        return Array.isArray(m) ? m.length : null;
                    }),
                ]);
                if (cancelled) return;
                const mc = {};
                teams.forEach((t, i) => { mc[t.id] = counts[i]; });
                setMemberCounts(mc);
                if (Array.isArray(rules)) {
                    const rc = {};
                    rules.filter(r => r.is_enabled).forEach(r => { const k = String(r.assigned_team_id); rc[k] = (rc[k] || 0) + 1; });
                    setRulesByTeam(rc);
                    setRulesLoaded(true);
                }
            } finally {
                if (!cancelled) setDetailsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [teams]);
    // --- END UPDATE

    // ── Filtered & Sorted (original logic preserved) ─────────────────────────
    const filteredAndSortedTeams = useMemo(() => {
        let filtered = teams.filter(team => {
            const matchesSearch = !searchTerm ||
                team.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                team.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                team.team_lead_username?.toLowerCase().includes(searchTerm.toLowerCase());
            const mc = memberCounts[team.id];
            const rc = rulesByTeam[String(team.id)] || 0;
            const matchesLeadFilter =
                filterLeadStatus === 'all' ||
                (filterLeadStatus === 'with_lead' && team.team_lead_id) ||
                (filterLeadStatus === 'no_lead' && !team.team_lead_id) ||
                // --- UPDATE: attention filters
                (filterLeadStatus === 'attention' && (!team.team_lead_id || mc === 0 || (rulesLoaded && rc > 0 && mc === 0))) ||
                (filterLeadStatus === 'no_members' && mc === 0) ||
                (filterLeadStatus === 'no_rules' && rulesLoaded && rc === 0);
            return matchesSearch && matchesLeadFilter;
        });
        filtered.sort((a, b) => {
            // --- UPDATE: numeric sorts for members / routing
            if (sortBy === 'members') return ((memberCounts[a.id] ?? -1) - (memberCounts[b.id] ?? -1)) * (sortOrder === 'asc' ? 1 : -1);
            if (sortBy === 'rules') return ((rulesByTeam[String(a.id)] || 0) - (rulesByTeam[String(b.id)] || 0)) * (sortOrder === 'asc' ? 1 : -1);
            let aVal = a[sortBy] || '';
            let bVal = b[sortBy] || '';
            if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
            return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
        });
        return filtered;
    }, [teams, searchTerm, filterLeadStatus, sortBy, sortOrder, memberCounts, rulesByTeam, rulesLoaded]);

    const teamStats = useMemo(() => ({
        total: teams.length,
        withLead: teams.filter(t => t.team_lead_id).length,
        noLead: teams.filter(t => !t.team_lead_id).length,
        noMembers: teams.filter(t => memberCounts[t.id] === 0).length, // --- UPDATE
    }), [teams, memberCounts]);

    // --- UPDATE: explainer
    const explainer = useMemo(() => {
        const findings = [];
        const routedEmpty = teams.filter(t => memberCounts[t.id] === 0 && (rulesByTeam[String(t.id)] || 0) > 0);
        const empty = teams.filter(t => memberCounts[t.id] === 0);
        const noLead = teams.filter(t => !t.team_lead_id);
        const unused = rulesLoaded ? teams.filter(t => !(rulesByTeam[String(t.id)] > 0)) : [];
        if (routedEmpty.length) findings.push({ severity: 'high', text: `${routedEmpty.length} team(s) receive rule-based work but have no members — those jobs reach nobody.`, evidence: routedEmpty.slice(0, 5).map(t => t.name).join(', ') });
        if (noLead.length) findings.push({ severity: 'medium', text: `${noLead.length} team(s) have no team lead, so escalations have no first owner.`, evidence: noLead.slice(0, 5).map(t => t.name).join(', ') });
        if (empty.length && empty.length !== routedEmpty.length) findings.push({ severity: 'medium', text: `${empty.length} team(s) have no members.`, evidence: empty.slice(0, 5).map(t => t.name).join(', ') });
        if (unused.length) findings.push({ severity: 'info', text: `${unused.length} team(s) are not used by any enabled assignment rule (they only get manual or AI-dispatched work).`, evidence: unused.slice(0, 5).map(t => t.name).join(', ') });
        if (teams.length && !findings.some(x => x.severity !== 'info')) findings.push({ severity: 'ok', text: 'Every team has a lead and members.' });
        return {
            findings,
            facts: {
                teams: teams.slice(0, 30).map(t => ({ name: t.name, lead: t.team_lead_username || null, members: memberCounts[t.id] ?? null, enabled_rules: rulesLoaded ? (rulesByTeam[String(t.id)] || 0) : null })),
                totals: { teams: teams.length, with_lead: teamStats.withLead, without_lead: teamStats.noLead, without_members: teamStats.noMembers },
            },
        };
    }, [teams, memberCounts, rulesByTeam, rulesLoaded, teamStats]);

    const handleExportCsv = useCallback(() => {
        const cell = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
        const lines = [['team', 'lead', 'members', 'enabled_rules', 'description'].join(',')]
            .concat(filteredAndSortedTeams.map(t => [t.name, t.team_lead_username || '', memberCounts[t.id] ?? '', rulesLoaded ? (rulesByTeam[String(t.id)] || 0) : '', t.description || ''].map(cell).join(',')));
        const url = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' }));
        const a = document.createElement('a'); a.href = url; a.download = `teams-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, [filteredAndSortedTeams, memberCounts, rulesByTeam, rulesLoaded]);
    // --- END UPDATE

    // ── Handlers (all original logic preserved) ──────────────────────────────
    const handleOpenCreateModal = useCallback(() => { setSelectedTeam(null); onFormOpen(); }, [onFormOpen]);
    const handleOpenEditModal   = useCallback((team) => { setSelectedTeam(team); onFormOpen(); }, [onFormOpen]);
    const handleViewMembers     = useCallback((team) => { setSelectedTeam(team); onMembersOpen(); }, [onMembersOpen]);
    const handleSave            = useCallback(() => { fetchTeamsData(); onFormClose(); }, [fetchTeamsData, onFormClose]);

    const handleDeleteTeam = useCallback(async () => {
        if (!teamToDelete) return;
        setIsLoading(true);
        try {
            await teamService.deleteTeam(teamToDelete.id);
            toast({ title:'Team deleted successfully', description:`"${teamToDelete.name}" has been removed`, status:'success', duration:3000, isClosable:true });
            fetchTeamsData();
            onDeleteClose();
        } catch (err) {
            const errorMessage = err?.response?.data?.message || err?.message || 'Failed to delete team';
            toast({ title:'Error deleting team', description:errorMessage, status:'error', duration:5000, isClosable:true });
        } finally {
            setIsLoading(false);
        }
    }, [teamToDelete, fetchTeamsData, onDeleteClose, toast]);

    const handleSort = useCallback((field) => {
        if (sortBy === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    }, [sortBy]);

    const SortIndicator = useCallback(({ field }) => {
        if (sortBy !== field) return null;
        return <Text as="span" ml={1} fontSize="xs">{sortOrder === 'asc' ? '↑' : '↓'}</Text>;
    }, [sortBy, sortOrder]);

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <Box>
            {/* ═══════ HEADER ═══════ */}
            <MotionFlex {...ANIM.fadeInDown} transition={ANIM.smooth}
                justifyContent="space-between" alignItems="center" mb={6}>
                <HStack spacing={4}>
                    <MotionBox initial={{ scale:0, rotate:-180 }} animate={{ scale:1, rotate:0 }}
                        transition={{ type:'spring', stiffness:200, damping:15 }}>
                        <Box bgGradient={headerGradient} p={3} borderRadius="xl"
                            boxShadow="0 4px 14px rgba(66,99,235,0.3)">
                            <Icon as={MdGroups} color="white" boxSize={6} />
                        </Box>
                    </MotionBox>
                    <Box>
                        <Heading size="md" fontWeight="800">Team Management</Heading>
                        <HStack spacing={2} mt={0.5}>
                            <Text color={mutedColor} fontSize="sm">Organize and manage field operation teams</Text>
                            <PulseIndicator color="green" size={1.5} />
                            <Text fontSize="xs" color={mutedColor} fontWeight="600">Live</Text>
                        </HStack>
                    </Box>
                </HStack>
                <HStack spacing={2}>
                    <Tooltip label="Refresh" hasArrow>
                        <IconButton icon={<Icon as={MdRefresh} />} variant="ghost" borderRadius="full"
                            onClick={fetchTeamsData} isLoading={isLoading} aria-label="Refresh"
                            _hover={{ transform:'rotate(180deg)' }} transition="all 0.3s" />
                    </Tooltip>
                    <MotionBox whileHover={{ scale:1.03 }} whileTap={{ scale:0.97 }}>
                        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={handleOpenCreateModal}
                            size="md" borderRadius="lg" boxShadow="0 4px 12px rgba(66,99,235,0.25)"
                            _hover={{ boxShadow:'0 6px 18px rgba(66,99,235,0.35)', transform:'translateY(-1px)' }}
                            transition="all 0.2s">
                            Create New Team
                        </Button>
                    </MotionBox>
                </HStack>
            </MotionFlex>

            {/* ═══════ STAT CARDS ═══════ */}
            <SimpleGrid columns={{ base:1, md:4 }} spacing={4} mb={6}> {/* --- UPDATE: + Without members */}
                <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(0), ...ANIM.smooth }}>
                    <EnhancedStatCard label="Total Teams" value={teamStats.total}
                        helpText="Active teams in system" icon={MdGroups} color="blue"
                        isLoading={isLoading && teams.length === 0} />
                </MotionBox>
                <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(1), ...ANIM.smooth }}>
                    <EnhancedStatCard label="With Team Lead" value={teamStats.withLead}
                        helpText={`${teamStats.total > 0 ? Math.round((teamStats.withLead / teamStats.total) * 100) : 0}% of teams`}
                        icon={MdStar} color="green" isLoading={isLoading && teams.length === 0} />
                </MotionBox>
                <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(2), ...ANIM.smooth }}>
                    <EnhancedStatCard label="No Team Lead" value={teamStats.noLead}
                        helpText="Needs assignment" icon={MdWarning} color="orange"
                        isLoading={isLoading && teams.length === 0} />
                </MotionBox>
                <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(3), ...ANIM.smooth }}>
                    <EnhancedStatCard label="Without Members" value={detailsLoading && !Object.keys(memberCounts).length ? '—' : teamStats.noMembers}
                        helpText="Teams nobody is in" icon={MdPersonAdd} color="red"
                        isLoading={isLoading && teams.length === 0} />
                </MotionBox>
            </SimpleGrid>

            {/* ═══════ UPDATE: AI EXPLAINER ═══════ */}
            <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(3), ...ANIM.smooth }} mb={6}>
                <FlowSenseAIExplainer
                    module="teams"
                    title="Are the teams ready to take work?"
                    subtitle="Leads, members and rule routing for every team — plus an AI explanation"
                    facts={explainer.facts}
                    findings={explainer.findings}
                    isLoading={(isLoading && teams.length === 0) || (detailsLoading && !Object.keys(memberCounts).length)}
                    defaultQuestion="Explain which teams are not ready to receive work and what to change first."
                    questions={['Which team should I fix first?', 'Is work being routed to empty teams?', 'How should I balance the teams?']}
                />
            </MotionBox>

            {/* ═══════ SEARCH / FILTER BAR ═══════ */}
            <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(3), ...ANIM.smooth }}>
                <Card mb={6} bg={bgColor} borderColor={borderColor} borderWidth="1px" borderRadius="xl"
                    boxShadow="sm">
                    <CardBody py={4}>
                        <Flex gap={3} wrap="wrap" align="center">
                            <InputGroup maxW="300px" size="sm">
                                <InputLeftElement pointerEvents="none">
                                    <SearchIcon color="gray.400" boxSize={3} />
                                </InputLeftElement>
                                <Input placeholder="Search teams…" value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    bg={searchBg} borderRadius="lg" fontSize="sm"
                                    _focus={{ borderColor:'blue.400', boxShadow:'0 0 0 1px var(--chakra-colors-blue-400)' }} />
                                {searchTerm && (
                                    <InputRightElement cursor="pointer" onClick={() => setSearchTerm('')}>
                                        <CloseIcon color="gray.400" boxSize={2} />
                                    </InputRightElement>
                                )}
                            </InputGroup>

                            <Select maxW="180px" size="sm" borderRadius="lg" value={filterLeadStatus}
                                onChange={e => setFilterLeadStatus(e.target.value)}>
                                <option value="all">All Teams</option>
                                <option value="with_lead">With Team Lead</option>
                                <option value="no_lead">No Team Lead</option>
                                {/* --- UPDATE */}
                                <option value="attention">Needs attention</option>
                                <option value="no_members">No members</option>
                                <option value="no_rules">Not used by rules</option>
                            </Select>

                            <Menu>
                                <MenuButton as={Button} rightIcon={<ChevronDownIcon />} variant="outline"
                                    size="sm" borderRadius="lg">
                                    Sort: {({ name: 'Name', team_lead_username: 'Lead', created_at: 'Date', members: 'Members', rules: 'Routing' })[sortBy] || 'Name'}
                                </MenuButton>
                                <MenuList borderRadius="xl" boxShadow="lg">
                                    <MenuItem onClick={() => handleSort('name')} borderRadius="md">
                                        Name <SortIndicator field="name" />
                                    </MenuItem>
                                    <MenuItem onClick={() => handleSort('team_lead_username')} borderRadius="md">
                                        Team Lead <SortIndicator field="team_lead_username" />
                                    </MenuItem>
                                    <MenuItem onClick={() => handleSort('created_at')} borderRadius="md">
                                        Created Date <SortIndicator field="created_at" />
                                    </MenuItem>
                                    {/* --- UPDATE */}
                                    <MenuItem onClick={() => handleSort('members')} borderRadius="md">
                                        Members <SortIndicator field="members" />
                                    </MenuItem>
                                    <MenuItem onClick={() => handleSort('rules')} borderRadius="md">
                                        Routing rules <SortIndicator field="rules" />
                                    </MenuItem>
                                </MenuList>
                            </Menu>

                            {/* --- UPDATE: CSV */}
                            <Button size="sm" variant="ghost" borderRadius="lg" onClick={handleExportCsv} isDisabled={!filteredAndSortedTeams.length}>Export CSV</Button>
                            {(searchTerm || filterLeadStatus !== 'all') && (
                                <MotionBox initial={{ opacity:0 }} animate={{ opacity:1 }} ml="auto">
                                    <Badge colorScheme="blue" borderRadius="full" px={3} py={1} fontSize="xs">
                                        {filteredAndSortedTeams.length} of {teams.length} teams
                                    </Badge>
                                </MotionBox>
                            )}
                        </Flex>
                    </CardBody>
                </Card>
            </MotionBox>

            {/* ═══════ DATA TABLE ═══════ */}
            <MotionBox {...ANIM.fadeInUp} transition={{ ...ANIM.stagger(4), ...ANIM.smooth }}>
                <TableContainer bg={bgColor} borderRadius="xl" borderWidth="1px" borderColor={borderColor}
                    boxShadow="sm" overflow="hidden">
                    {isLoading && <Progress size="xs" isIndeterminate colorScheme="blue" />}
                    <Table variant="simple">
                        <Thead>
                            <Tr>
                                <Th cursor="pointer" onClick={() => handleSort('name')}
                                    _hover={{ color:'blue.500' }} transition="all 0.2s">
                                    Team Name <SortIndicator field="name" />
                                </Th>
                                <Th>Description</Th>
                                <Th cursor="pointer" onClick={() => handleSort('team_lead_username')}
                                    _hover={{ color:'blue.500' }} transition="all 0.2s">
                                    Team Lead <SortIndicator field="team_lead_username" />
                                </Th>
                                <Th>Members</Th>
                                <Th>Routing</Th>{/* --- UPDATE */}
                                <Th>Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {isLoading && teams.length === 0 && <TableSkeleton rows={4} cols={6} />}
                            {error && !isLoading && (
                                <Tr><Td colSpan={6}>
                                    <Alert status="error" borderRadius="lg">
                                        <AlertIcon />{error}
                                    </Alert>
                                </Td></Tr>
                            )}
                            {!isLoading && !error && filteredAndSortedTeams.length === 0 && (
                                <Tr><Td colSpan={6}>
                                    <EmptyState icon={MdGroups}
                                        title={searchTerm || filterLeadStatus !== 'all'
                                            ? 'No teams match your filters' : 'No teams created yet'}
                                        subtitle="Create a new team to get started" />
                                </Td></Tr>
                            )}
                            <AnimatePresence>
                                {!isLoading && !error && filteredAndSortedTeams.map((team, idx) => (
                                    <MotionTr key={team.id}
                                        initial={{ opacity:0, x:-10 }}
                                        animate={{ opacity:1, x:0 }}
                                        exit={{ opacity:0, x:20 }}
                                        transition={{ ...ANIM.stagger(idx, 0.03), ...ANIM.smooth }}
                                        _hover={{ bg:trHoverBg }}
                                        style={{ transition:'background 0.15s' }}
                                    >
                                        <Td>
                                            <VStack align="start" spacing={0.5}>
                                                <Text fontWeight="700" fontSize="sm">{team.name}</Text>
                                                {team.created_at && (
                                                    <Text fontSize="2xs" color={mutedColor}>
                                                        Created {new Date(team.created_at).toLocaleDateString()}
                                                    </Text>
                                                )}
                                            </VStack>
                                        </Td>
                                        <Td>
                                            <Tooltip label={team.description}
                                                isDisabled={!team.description || team.description.length < 50}
                                                hasArrow placement="top">
                                                <Text noOfLines={2} maxW="300px" fontSize="sm">
                                                    {team.description || (
                                                        <Text as="span" color="gray.400" fontStyle="italic" fontSize="sm">
                                                            No description
                                                        </Text>
                                                    )}
                                                </Text>
                                            </Tooltip>
                                        </Td>
                                        <Td>
                                            {team.team_lead_id ? (
                                                <HStack spacing={2}>
                                                    <Avatar size="xs" name={team.team_lead_username}
                                                        bg="green.500" color="white" />
                                                    <Text fontSize="sm" fontWeight="600">{team.team_lead_username}</Text>
                                                </HStack>
                                            ) : (
                                                <Badge colorScheme="orange" borderRadius="full" variant="subtle"
                                                    fontSize="2xs" px={2}>
                                                    No Lead Assigned
                                                </Badge>
                                            )}
                                        </Td>
                                        <Td>
                                            <HStack spacing={2}>
                                                {/* --- UPDATE: real member count */}
                                                {memberCounts[team.id] === undefined ? (
                                                    detailsLoading ? <Skeleton h="16px" w="28px" borderRadius="full" /> : <Badge variant="outline" borderRadius="full">—</Badge>
                                                ) : (
                                                    <Badge colorScheme={memberCounts[team.id] === 0 ? 'red' : 'blue'} borderRadius="full" variant="subtle">
                                                        {memberCounts[team.id] === null ? '—' : memberCounts[team.id]}
                                                    </Badge>
                                                )}
                                                <Button size="xs" variant="ghost" leftIcon={<ViewIcon />}
                                                    onClick={() => handleViewMembers(team)} borderRadius="lg"
                                                    _hover={{ bg:'blue.50', color:'blue.600' }} transition="all 0.2s">
                                                    Manage
                                                </Button>
                                            </HStack>
                                        </Td>
                                        <Td>
                                            {/* --- UPDATE: enabled rules routing to this team */}
                                            {!rulesLoaded ? (
                                                detailsLoading ? <Skeleton h="16px" w="56px" borderRadius="full" /> : <Text fontSize="xs" color={mutedColor}>—</Text>
                                            ) : (rulesByTeam[String(team.id)] || 0) > 0 ? (
                                                <Tooltip label={memberCounts[team.id] === 0 ? 'Rules send work here but the team has no members' : 'Enabled assignment rules for this team'} hasArrow>
                                                    <Badge colorScheme={memberCounts[team.id] === 0 ? 'red' : 'purple'} borderRadius="full" variant="subtle">
                                                        {rulesByTeam[String(team.id)]} rule{rulesByTeam[String(team.id)] === 1 ? '' : 's'}
                                                    </Badge>
                                                </Tooltip>
                                            ) : (
                                                <Text fontSize="xs" color={mutedColor}>No rules</Text>
                                            )}
                                        </Td>
                                        <Td>
                                            <HStack spacing={1}>
                                                <Tooltip label="Edit Team" hasArrow>
                                                    <IconButton icon={<EditIcon />} size="xs" aria-label="Edit Team"
                                                        variant="ghost" borderRadius="full"
                                                        onClick={() => handleOpenEditModal(team)}
                                                        _hover={{ bg:'blue.50', color:'blue.600' }} />
                                                </Tooltip>
                                                <Tooltip label="Delete Team" hasArrow>
                                                    <IconButton icon={<DeleteIcon />} size="xs" colorScheme="red"
                                                        variant="ghost" aria-label="Delete Team" borderRadius="full"
                                                        onClick={() => { setTeamToDelete(team); onDeleteOpen(); }}
                                                        _hover={{ bg:'red.50' }} />
                                                </Tooltip>
                                            </HStack>
                                        </Td>
                                    </MotionTr>
                                ))}
                            </AnimatePresence>
                        </Tbody>
                    </Table>
                </TableContainer>
            </MotionBox>

            {/* ═══════ MODALS (original logic preserved) ═══════ */}
            <TeamFormModal isOpen={isFormOpen} onClose={onFormClose} onSave={handleSave} currentTeam={selectedTeam} />

            <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}
                motionPreset="slideInBottom">
                <AlertDialogOverlay backdropFilter="blur(4px)">
                    <AlertDialogContent borderRadius="xl">
                        <AlertDialogHeader fontSize="md" fontWeight="700">
                            <HStack spacing={2}>
                                <Icon as={MdWarning} color="red.500" />
                                <Text>Delete Team</Text>
                            </HStack>
                        </AlertDialogHeader>
                        <AlertDialogBody fontSize="sm">
                            Are you sure you want to delete the team "<Text as="span" fontWeight="700">{teamToDelete?.name}</Text>"?
                            This action cannot be undone. Team members will need to be reassigned.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onDeleteClose} size="sm" borderRadius="lg">Cancel</Button>
                            <Button colorScheme="red" onClick={handleDeleteTeam} ml={3} isLoading={isLoading}
                                size="sm" borderRadius="lg" leftIcon={<DeleteIcon />}>Delete</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            <TeamMembersModal isOpen={isMembersOpen} onClose={onMembersClose}
                currentTeam={selectedTeam} onUpdate={fetchTeamsData} />
        </Box>
    );
};

export default TeamManagementComponent;